import React, { useState, useEffect } from 'react';
import {
  TextField,
  IconButton,
  InputAdornment,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box
} from '@mui/material';
import ByzMuiDataGrid from "../data-grid";
import { GridColDef, GridRowSelectionModel } from '@mui/x-data-grid-premium';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import ClearIcon from '@mui/icons-material/Clear';
import { useTranslate } from 'src/locales/use-locales';
import { baseService } from 'src/api/core/baseService';

interface SimpleSelectListPopupProps {
  url: string;
  method?: "GET" | "POST";
  postData?: any;
  label?: string;
  value?: string;
  onChange?: (value: any) => void;
  dialogTitle?: string;
  displayField?: string;
  columns?: GridColDef[];
  size?: "small" | "medium";
  fullWidth?: boolean;
  disabled?: boolean;
  placeholder?: string;
}

const SimpleSelectListPopup: React.FC<SimpleSelectListPopupProps> = ({
  url,
  method = "GET",
  postData,
  label,
  value = "",
  onChange,
  dialogTitle,
  displayField = "NAME",
  columns,
  size = "small",
  fullWidth = false,
  disabled = false,
  placeholder
}) => {
  const { t } = useTranslate();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any[]>([]);
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);

  const handleOpen = () => {
    if (!disabled) {
      setSelectionModel([]);
      setOpen(true);
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleOk = () => {
    if (selectionModel.length > 0) {
      const selectedRowsData = data.filter(row => selectionModel.includes(row.id));
      onChange?.(selectedRowsData[0]); // Tek seçim için ilk elemanı al
    }
    handleClose();
  };

  const handleClear = () => {
    onChange?.(null);
  };

  const defaultColumns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 100 },
    { field: displayField, headerName: 'Adı', flex: 1 },
  ];

  const dataColumns = columns || defaultColumns;

  useEffect(() => {
    if (!open) return;

    setLoading(true);
    const fetchData = async () => {
      try {
        let res: any;
        if (method === "GET") {
          res = await baseService.getAll(url);
        } else {
          res = await baseService.add(url, postData);
        }

        let rawList: any[] = [];
        if (res?.DbResponse?.Result) {
          rawList = res.DbResponse.Result;
        } else if (res?.result?.data) {
          rawList = res.result.data;
        } else if (res?.data) {
          rawList = res.data;
        } else if (Array.isArray(res)) {
          rawList = res;
        }

        const result = (rawList ?? []).map((row: any) => ({
          ...row,
          id: String(row.ID ?? row.id),
          // NAME field'ını name'den de alabilir
          NAME: row.NAME || row.name,
        }));

        setData(result);
      } catch (err) {
        console.error("Veriyi çekerken hata oluştu:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [open, url, method, postData]);

  return (
    <>
      <Tooltip title={value || ""} disableHoverListener={!value}>
        <span>
          <TextField
            size={size}
            label={label}
            value={value}
            placeholder={placeholder}
            variant="outlined"
            color="primary"
            disabled={disabled}
            fullWidth={fullWidth}
            InputLabelProps={{ shrink: true }}
            inputProps={{
              readOnly: true,
              style: { cursor: disabled ? "default" : "pointer" },
            }}
            onClick={handleOpen}
            sx={{
              "& .MuiInputLabel-root": {
                color: "#2498f3 !important",
              },
              "& .MuiInputLabel-root.Mui-focused": {
                color: "#2498f3 !important",
              },
              "& .MuiFormLabel-root": {
                color: "#2498f3 !important",
              },
              "& .MuiFormLabel-root.Mui-focused": {
                color: "#2498f3 !important",
              },
              "& .MuiOutlinedInput-root": {
                height: size === "small" ? "36px" : "auto",
                "& fieldset": {
                  borderColor: "#2498f3",
                },
                "&:hover fieldset": {
                  borderColor: "#2498f3",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#2498f3",
                },
              },
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {value && !disabled && (
                    <IconButton
                      edge="end"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleClear();
                      }}
                      sx={{ 
                        mr: 0.5,
                        width: 24,
                        height: 24,
                        minWidth: 24,
                        minHeight: 24
                      }}
                    >
                      <ClearIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  )}
                  <IconButton
                    edge="end"
                    disabled={disabled}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpen();
                    }}
                    sx={{
                      width: 24,
                      height: 24,
                      minWidth: 24,
                      minHeight: 24
                    }}
                  >
                    <MoreHorizIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </span>
      </Tooltip>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {dialogTitle || t("buttons.chose")}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ height: 400, width: '100%', mt: 1 }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <CircularProgress />
              </Box>
            ) : (
                             <ByzMuiDataGrid
                 rows={data}
                 columns={dataColumns}
                 onRowClick={(params) => {
                   setSelectionModel([params.row.id]);
                 }}
                 onRowDoubleClick={(params) => {
                   setSelectionModel([params.row.id]);
                   const selectedRowsData = data.filter(row => row.id === params.row.id);
                   onChange?.(selectedRowsData[0]);
                   handleClose();
                 }}
                 rowSelectionModel={selectionModel}
                 pageSizeOptions={[10, 25, 50]}
                 initialState={{
                   pagination: {
                     paginationModel: { page: 0, pageSize: 10 },
                   },
                 }}
                 sx={{
                   '& .MuiDataGrid-row': {
                     cursor: 'pointer',
                     '&:hover': {
                       backgroundColor: '#f5f5f5',
                     },
                     '&.Mui-selected': {
                       backgroundColor: '#e3f2fd',
                       '&:hover': {
                         backgroundColor: '#bbdefb',
                       },
                     },
                   },
                 }}
               />
            )}
          </Box>
        </DialogContent>
                 <DialogActions>
           <Button onClick={handleClose}>
             {t("buttons.cancel")}
           </Button>
           <Button onClick={handleOk} variant="contained">
             {t("buttons.okey")}
           </Button>
         </DialogActions>
      </Dialog>
    </>
  );
};

export default SimpleSelectListPopup;
